import { CommonModule } from '@angular/common';
import { Component, inject, signal, type WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LottieComponent, type AnimationOptions } from 'ngx-lottie';
import { Card } from '../../../shared/components/card/card/card';
import { CardHeader } from '../../../shared/components/card/card-header/card-header';
import { Navbar } from '../../../shared/components/navbar/navbar';
import type { UtmBettorRecord, UtmBettorsMeta } from '../../../interfaces/utm';
import { UtmMetricsService } from '../../../services/utm-metrics.service';

@Component({
  selector: 'app-utm-bettors',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    FloatLabelModule,
    InputTextModule,
    PaginatorModule,
    ProgressSpinnerModule,
    TableModule,
    TagModule,
    LottieComponent,
    Card,
    CardHeader,
    Navbar,
  ],
  templateUrl: './utm-bettors.html',
})
export class UtmBettors {
  public readonly emptyLottieOptions: AnimationOptions = {
    path: 'assets/lottie/empty.json',
    loop: true,
    autoplay: true,
  };

  private readonly _messageService = inject(MessageService);
  private readonly _utmMetricsService = inject(UtmMetricsService);

  public readonly loading: WritableSignal<boolean> = signal(false);
  public readonly firstRequest: WritableSignal<boolean> = signal(true);
  public readonly data: WritableSignal<UtmBettorRecord[]> = signal([]);
  public readonly meta: WritableSignal<UtmBettorsMeta | null> = signal(null);
  public readonly totalFtdOnPage: WritableSignal<number> = signal(0);
  public readonly totalDepositsOnPage: WritableSignal<number> = signal(0);
  public readonly totalWithdrawalsOnPage: WritableSignal<number> = signal(0);

  public page = 1;
  public rows = 50;
  public totalRecords = 0;

  public readonly filterForm = new FormGroup({
    start_date: new FormControl<Date | null>(this._getMonthStart()),
    end_date: new FormControl<Date | null>(new Date()),
    user_id: new FormControl<string>(''),
  });

  public ngOnInit(): void {
    this.getData();
  }

  public applyFilters(): void {
    this.page = 1;
    this.getData();
  }

  public onPageChange(event: PaginatorState): void {
    this.page = (event.page || 0) + 1;
    this.rows = event.rows || this.rows;
    this.getData();
  }

  public hasFtd(item: UtmBettorRecord): boolean {
    return Number(item.fez_ftd) > 0;
  }

  public getData(): void {
    const { start_date, end_date, user_id } = this.filterForm.getRawValue();

    if (!start_date || !end_date) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Período obrigatório',
        detail: 'Selecione a data inicial e final para consultar os apostadores.',
        life: 3000,
      });
      return;
    }

    if (end_date < start_date) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Período inválido',
        detail: 'A data final não pode ser menor que a data inicial.',
        life: 3000,
      });
      return;
    }

    this.loading.set(true);
    this.firstRequest.set(false);

    this._utmMetricsService.queryBettors({
      start_date: this._formatDate(start_date),
      end_date: this._formatDate(end_date),
      user_id: user_id?.trim() || undefined,
      page: this.page,
      per_page: this.rows,
    })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.data.set(response.data || []);
          this.meta.set(response.meta);
          this.page = response.meta?.current_page || this.page;
          this.rows = response.meta?.per_page || this.rows;
          this.totalRecords = response.meta?.total_records || 0;
          this._updatePageSummaries(response.data || []);
        },
        error: (error) => {
          this.data.set([]);
          this.meta.set(null);
          this.totalRecords = 0;
          this._updatePageSummaries([]);
          this._messageService.add({
            severity: 'error',
            summary: 'Falha ao consultar apostadores',
            detail: error?.error?.message || 'Não foi possível carregar as métricas de apostadores.',
            life: 3000,
          });
        },
      });
  }

  private _updatePageSummaries(data: UtmBettorRecord[]): void {
    this.totalFtdOnPage.set(data.filter(item => this.hasFtd(item)).length);
    this.totalDepositsOnPage.set(data.reduce((acc, item) => acc + (item.valor_depositos || 0), 0));
    this.totalWithdrawalsOnPage.set(data.reduce((acc, item) => acc + (item.valor_saques || 0), 0));
  }

  private _formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private _getMonthStart(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }
}